import React, { useEffect, useRef, useState } from "react";
import "../../_css/Feedback.css";
import { notifyError, notifySuccess } from "../helper/Toaster";
import {
  createPDFFeedback,
  createPDFFeedbackWf,
  getAllPdfs,
  sharePDF,
  updateFeedback,
} from "../../requests";
import { useDispatch } from "react-redux";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer";
import { PencilIcon } from "lucide-react";

const Feedback = ({ fn, name = "", disabled = true, save = true, id }) => {
  const dispatch = useDispatch();
  const [strengths_, setStrengths] = useState([]);
  const [weaknesses_, setWeaknesses] = useState([]);
  const [recommendations_, setRecommendations] = useState([]);
  const [student_name, setStudentName] = useState("");
  const [subject, setSubject] = useState("");
  const [grade, setGrade] = useState("");
  

  const divRef = useRef(null);
  const [pdf, setPdf] = useState(null);
  /** Workflow marked PDF URL — used for single-file merge via /create-pdf-feedback-wf */
  const [pdfWfMarked, setPdfWfMarked] = useState(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [includeAnswerSheet, setIncludeAnswerSheet] = useState(false);
  const textAreaRefs = useRef({});

  const data = [
    {
      title: "strengths",
      desc: strengths_,
    },
    {
      title: "weaknesses",
      desc: weaknesses_,
    },

    {
      title: "recommendations",
      desc: recommendations_,
    },
  ];

  const savehandler = async (type = 0) => {
    if (disabled) {
      dispatch(startLoader());
      await new Promise((resolve) => setTimeout(resolve, 1000));
      notifySuccess("Feedback saved successfully");
      dispatch(stopLoader());
      // fn();
      return;
    }

    dispatch(startLoader());

    try {
      const response = await updateFeedback({
        id: id,
        strengths: strengths_,
        weaknesses: weaknesses_,
        recommendations: recommendations_,
      });
      if (response.status < 400) {
        if (type == 1) {
          notifySuccess("Feedback saved successfully");
        }

        // fn();
      }
    } catch (error) {
      notifyError("Server busy please try again..");
    } finally {
      dispatch(stopLoader());
    }
  };

  const onchangehandler = (e, i) => {
    if (e.target.name == "strengths") {
      const newItems = [...strengths_];
      newItems[i] = e.target.value;
      setStrengths(newItems);
    }

    if (e.target.name === "weaknesses") {
      const newItems = [...weaknesses_];
      newItems[i] = e.target.value;
      setWeaknesses(newItems);
    }

    if (e.target.name === "recommendations") {
      const newItems = [...recommendations_];
      newItems[i] = e.target.value;
      setRecommendations(newItems);
    }
  };

  const get_pdf = async () => {
    dispatch(startLoader());
    try {
      const response = await getAllPdfs({ id: id });
      if (response.status < 400) {
        setStrengths(response.data.data.strengths);
        setWeaknesses(response.data.data.mistakes);
        setRecommendations(response.data.data.improvements);
        setStudentName(response.data.data.student_name);
        setPdf(response.data.data.answersheeturlmarked || response.data.data.answersheeturl);
        setPdfWfMarked(response.data.data.answersheeturlmarkedwf || null);

        setSubject(response.data.data.subject);
        setGrade(response.data.data.grade);

        if (response.data.data.strengths.length == 0) {
          setStrengths(["", "", ""]);
        }
        if (response.data.data.mistakes.length == 0) {
          setWeaknesses(["", "", ""]);
        }
        if (response.data.data.improvements.length == 0) {
          setRecommendations(["", "", ""]);
        }
      }
    } catch (error) {
      console.log(error);

      notifyError("Something went wrong");
    } finally {
      dispatch(stopLoader());
    }
  };

  /** Download file from a remote URL (e.g. AI marked answer sheet). */
  const downloadPdfFromUrl = async (url, filename) => {
    if (!url) {
      notifyError("Answer sheet PDF is not available.");
      return;
    }
    try {
      const res = await fetch(url, { mode: "cors" });
      if (!res.ok) throw new Error("fetch failed");
      const blob = await res.blob();
      const objUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(objUrl);
    } catch (err) {
      console.log(err);
      // Cross-origin or CORS: open in new tab so user can save manually
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();
      notifySuccess("Answer sheet opened in a new tab — use Save as to download if needed.");
    }
  };

  const downLaodPdf = async (share = false) => {
    if (save) {
      await savehandler();
    }

    dispatch(startLoader());

    try {
      const response = await createPDFFeedback({ id: id });

      const file = new Blob([response.data], { type: "application/pdf" });
      if (share) {
        return file;
      }

      const url = window.URL.createObjectURL(file);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${student_name}_${subject}_feedback.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log(error);
      notifyError("Server busy please try again..");
    } finally {
      dispatch(stopLoader());
    }
  };

  const openDownloadPdfModal = () => {
    setIncludeAnswerSheet(false);
    setShowDownloadModal(true);
  };

  const handleConfirmDownloadPdf = async () => {
    const sheet = includeAnswerSheet;
    const hasWfMarked = Boolean(pdfWfMarked);
    const hasLegacySheet = Boolean(pdf);
    setShowDownloadModal(false);
    setIncludeAnswerSheet(false);

    if (sheet && hasWfMarked) {
      if (save) {
        await savehandler();
      }
      dispatch(startLoader());
      try {
        const response = await createPDFFeedbackWf({ id });
        const file = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(file);
        const a = document.createElement("a");
        a.href = url;
        const safeName = (student_name || "student").replace(/[/\\?%*:|"<>]/g, "-");
        const safeSubject = (subject || "subject").replace(/[/\\?%*:|"<>]/g, "-");
        a.download = `${safeName}_${safeSubject}_feedback_with_marked_answer_sheet.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.log(error);
        notifyError("Could not download merged PDF. Try again or download feedback only.");
      } finally {
        dispatch(stopLoader());
      }
      return;
    }

    await downLaodPdf(false);

    if (sheet) {
      if (hasLegacySheet) {
        const safeName = (student_name || "student").replace(/[/\\?%*:|"<>]/g, "-");
        const safeSubject = (subject || "subject").replace(/[/\\?%*:|"<>]/g, "-");
        await downloadPdfFromUrl(
          pdf,
          `${safeName}_${safeSubject}_AI_marked_answer_sheet.pdf`
        );
      } else {
        notifyError("AI marked answer sheet is not available for this submission.");
      }
    }
  };

  const downLaodWord = async (share = false) => {
    dispatch(startLoader());

    if (save) {
      await savehandler();
    }

    try {
      const response = await createPDFFeedback({ id: id, type: 1 });

      // Create file blob
      const file = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });

      // Create download link
      const url = window.URL.createObjectURL(file);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${student_name}_${subject}_feedback.docx`; // file name
      document.body.appendChild(a);
      a.click();

      // Cleanup
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log(error);
      notifyError("Server busy please try again..");
    } finally {
      dispatch(stopLoader());
    }
  };

  const sharehandler = async () => {
   
    try {
      const pdf = await downLaodPdf(true);

      const pdfFile =
        pdf &&
        new File([pdf], `${student_name}_${subject}_feedback.pdf`, {
          type: "application/pdf",
        });

      const formData = new FormData();


      formData.append("pdf", pdfFile);
      formData.append("grade", grade);
      formData.append("student_name", student_name);
      formData.append("subject", subject);
      formData.append("type",1)

      
dispatch(startLoader());
      const res = await sharePDF(formData);
      if (res.status < 400) {
        notifySuccess("Feedback report sent to your registered email successfully");
      }
    } catch (error) {
      console.log(error);
      notifyError("Server busy please try again..");
    } finally {
      dispatch(stopLoader());
    }
  };

  useEffect(() => {
    get_pdf();
  }, []);

  return (
    <div className="feedback-main">
      <div className="feedback-con">
        <div className="feedback-head">
          <div className="feedback-head-txt">
            {name ? name + " Feedback" : "Feedback"}
          </div>
          <img
            onClick={() => fn()}
            src="/close.svg"
            width="18px"
            height="18px"
            style={{ cursor: "pointer" }}
          />
        </div>

        <div className="feedback-body" ref={divRef}>
          {data.map((e, i) => (
            <div key={i} className="feedback-box">
              <div className="feedback-box-title">{e.title.charAt(0).toUpperCase() + e.title.slice(1)}</div>
              <div className="feedback-box-desc">
                {e.desc.map((d, i) => (
                  <div className="feedback-textarea-wrapper" key={i}>
                    &bull;
                    <div className="textarea-icon-container">
                      <textarea
                        ref={el => {
                          if (!textAreaRefs.current[e.title]) textAreaRefs.current[e.title] = [];
                          textAreaRefs.current[e.title][i] = el;
                        }}
                        name={e.title}
                        disabled={disabled}
                        style={{
                          width: "100%",
                          border: "none",
                          background: "transparent",
                          color: "#6B7280",
                          resize: "none",
                          outlineColor: "#6B7280",
                          paddingRight: !disabled ? "30px" : undefined,
                        }}
                        value={d}
                        placeholder={`Enter ${e.title}`}
                        onChange={(e) => onchangehandler(e, i)}
                        onFocus={(e) => {
                          e.target.style.backgroundColor = "white";
                        }}
                        onBlur={(e) => {
                          e.target.style.backgroundColor = "transparent";
                        }}
                      />
                      {
                        !disabled && (
                          <span
                            className="pencil-icon"
                            onClick={() => textAreaRefs.current[e.title] && textAreaRefs.current[e.title][i] && textAreaRefs.current[e.title][i].focus()}
                            style={{ cursor: "pointer", pointerEvents: "auto" }}
                          >
                            <PencilIcon size={16} />
                          </span>
                        )
                      }
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="feedback-submit">
          <button
            className="feedback-submit-btn"
            onClick={() => sharehandler()}
          >
            Share
          </button>
          <button
            className="feedback-submit-btn"
            onClick={openDownloadPdfModal}
          >
            Download PDF
          </button>
          <button className="feedback-submit-btn" onClick={() => downLaodWord()}>
            Download Word
          </button>
         

          
          
          

          {save && (
            <button
              onClick={() => savehandler(1)}
              className="feedback-submit-btn"
              style={{ backgroundColor: "#01A7DE", color: "#FFFFFF" }}
            >
              Save as draft
            </button>
          )}
        </div>
      </div>

      {showDownloadModal && (
        <div
          className="feedback-download-modal-overlay"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowDownloadModal(false);
          }}
        >
          <div
            className="feedback-download-modal"
            role="dialog"
            aria-labelledby="feedback-download-title"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="feedback-download-modal-title" id="feedback-download-title">
              Download Feeback PDF
            </div>
            <p className="feedback-download-modal-desc">
               AI generated feedback report
            </p>
            <label className="feedback-download-modal-checkbox-row">
              <input
                type="checkbox"
                checked={includeAnswerSheet}
                onChange={(e) => setIncludeAnswerSheet(e.target.checked)}
                disabled={!pdfWfMarked && !pdf}
              />
              <span>
                Include marked answer sheet with the feedback report
                {!pdfWfMarked && !pdf && (
                  <span className="feedback-download-modal-hint">
                    {" "}
                    (not available for this submission)
                  </span>
                )}
              </span>
            </label>
            <div className="feedback-download-modal-actions">
              <button
                type="button"
                className="feedback-download-modal-btn feedback-download-modal-btn-secondary"
                onClick={() => {
                  setShowDownloadModal(false);
                  setIncludeAnswerSheet(false);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="feedback-download-modal-btn feedback-download-modal-btn-primary"
                onClick={handleConfirmDownloadPdf}
              >
                Download
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Feedback;
